<template>
    <main class="min-h-screen bg-white  overflow-hidden flex flex-col">
      <!-- Background elements -->
      <UnderwaterBg class="fixed inset-0 z-0" />
      
      <!-- Header/Navigation -->
      <the-header
        :is-user-authenticated="isUserAuthenticated"
      />
  
      <!-- Main Content -->
      <div class="relative z-10 px-6 md:px-10 max-w-7xl mx-auto py-12 md:py-20 flex-grow flex items-center justify-center">
        <div v-if="loading" class="text-center py-20 w-full max-w-md">
          <div class="flex flex-col items-center">
            <img 
              src="/ducksImages/duckLoader.webp" 
              alt="Loading Duck" 
              class="w-32 h-32 mb-6 animate-bounce-slow"
            />
            <h2 class="text-2xl font-bold text-slate-800  mb-4">
              Verifying Payment
            </h2>
            <p class="text-gray-600  mb-6">
              Please wait while we confirm your purchase details...
            </p>
            <div class="w-full bg-gray-200  h-2 rounded-full overflow-hidden">
              <div 
                class="bg-yellow-500 h-full rounded-full loading-progress"
              ></div>
            </div>
          </div>
        </div>
        
        <Motion
          v-else
          :initial="{ opacity: 0, y: 20 }"
          :animate="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.5 }"
          class="w-full max-w-2xl backdrop-blur-sm bg-white/80  rounded-xl p-8 shadow-lg text-center"
        >
          <div v-if="paymentSuccess" class="py-8">
            <v-icon
              color="success"
              size="64"
              class="mb-6"
            >
              mdi-check-circle
            </v-icon>
            
            <h1 class="text-3xl font-bold mb-4 text-slate-800 ">
              Payment Successful!
            </h1>
            
            <p class="text-lg mb-8 text-gray-700 ">
              Thank you for your purchase! Your order has been confirmed and your cruise is now active.
            </p>
            
            <div class="bg-green-50  p-6 rounded-lg mb-8">
              <h3 class="font-semibold text-lg mb-4 text-slate-800 ">
                What's Next?
              </h3>
              <p class="text-gray-700  mb-4">
                You can now access your new cruise from your dashboard. From there, you can:
              </p>
              <ul class="text-left mb-4 text-gray-700  space-y-2 pl-4">
                <li class="flex items-center gap-2">
                  <v-icon color="green" size="small">mdi-check-circle</v-icon>
                  <span>Manage your ducks</span>
                </li>
                <li class="flex items-center gap-2">
                  <v-icon color="green" size="small">mdi-check-circle</v-icon>
                  <span>Customize your cruise settings</span>
                </li>
                <li class="flex items-center gap-2">
                  <v-icon color="green" size="small">mdi-check-circle</v-icon>
                  <span>Track your duck adventures</span>
                </li>
              </ul>
            </div>
            
            <div class="flex flex-wrap justify-center gap-4">
              <v-btn
                color="primary"
                variant="elevated"
                size="large"
                to="/admin"
                prepend-icon="mdi-view-dashboard"
              >
                Go to Dashboard
              </v-btn>
              
              <v-btn
                variant="outlined"
                size="large"
                to="/"
                prepend-icon="mdi-home"
              >
                Return to Home
              </v-btn>
            </div>
          </div>
          
          <div v-else class="py-8">
            <v-icon
              color="error"
              size="64"
              class="mb-6"
            >
              mdi-alert-circle
            </v-icon>
            
            <h1 class="text-3xl font-bold mb-4 text-slate-800 ">
              Payment Verification Failed
            </h1>
            
            <p class="text-lg mb-8 text-gray-700 ">
              We couldn't verify your payment. If you believe this is an error, please contact our support team.
            </p>
            
            <div class="flex flex-wrap justify-center gap-4">
              <v-btn
                color="primary"
                variant="elevated"
                size="large"
                to="/#pricing"
                prepend-icon="mdi-cart"
              >
                Try Again
              </v-btn>
              
              <v-btn
                variant="outlined"
                size="large"
                to="/contact"
                prepend-icon="mdi-email"
              >
                Contact Support
              </v-btn>
            </div>
          </div>
        </Motion>
      </div>
  
      <!-- Footer -->
      <the-footer />
  
      <!-- Notifications -->
      <v-snackbar
        v-model="showNotification"
        :color="notificationColor"
        :timeout="3000"
        location="top"
      >
        {{ notificationMessage }}
      </v-snackbar>
    </main>
  </template>
  
  <script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import axios from 'axios';
  import { UnderwaterBg } from '@/components/ui/underwaterBg';
  import { useAuthStore } from '@/stores/authStore';
  import { Motion } from 'motion-v';
  import TheHeader from '@/components/layout/TheHeader.vue';
  import TheFooter from '@/components/layout/TheFooter.vue';
  import { Constant } from '@/config/constants';
  
  const route = useRoute();
  const router = useRouter();
  const authStore = useAuthStore();
  
  // State
  const loading = ref(true);
  const paymentSuccess = ref(false);
  const showNotification = ref(false);
  const notificationMessage = ref('');
  const notificationColor = ref('info');
  const loadingState = ref(0);
  
  const sessionId = computed(() => {
    // Get session_id from query parameters regardless of which route was used
    return route.query.session_id as string;
  });
  
  // Loading steps
  const loadingSteps = [
    { text: 'Connecting to payment system' },
    { text: 'Verifying payment status' },
    { text: 'Activating your subscription' },
    { text: 'Setting up your account' }
  ];
  
  // Computed property to check if user is authenticated
  const isUserAuthenticated = computed(() => {
    return authStore.isAuthenticated();
  });
  
  const simulateLoading = async () => {
    const steps = loadingSteps.length;
    const stepTime = 1000; // Time for each step in ms
    
    for (let i = 0; i < steps; i++) {
      loadingState.value = i;
      await new Promise(resolve => setTimeout(resolve, stepTime));
    }
  };
  
  // Verify payment on component mount
  onMounted(async () => {
    
    // If user isn't authenticated, redirect to login
    if (!isUserAuthenticated.value) {
      router.push({
        path: '/',
        query: { 
          showLogin: 'true',
          redirect: route.fullPath,
          message: 'Please log in to view your payment status'
        }
      });
      return;
    }
    
    // If no session ID, redirect to home
    if (!sessionId.value) {
      router.push('/');
      return;
    }
    
    try {
      // Start loading animation
      loading.value = true;
      await simulateLoading();
      
      // Use the endpoint to verify the payment
      const response = await axios.get(`${Constant.API_URL}/checkout/success?session_id=${sessionId.value}`);
      
      if (response.status === 200) {
        paymentSuccess.value = true;
      } else {
        paymentSuccess.value = false;
        notificationMessage.value = 'We couldn\'t verify your payment status.';
        notificationColor.value = 'error';
        showNotification.value = true;
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      notificationMessage.value = 'An error occurred while verifying your payment.';
      notificationColor.value = 'error';
      showNotification.value = true;
      paymentSuccess.value = false;
    } finally {
      loading.value = false;
    }
  });
  </script>
  
  <style scoped>
  /* Add custom styles for payment success page */
  .animate-bounce-slow {
    animation: bounce 2s ease-in-out infinite;
  }
  
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-15px); }
  }
  
  .loading-progress {
    width: 90%;
    animation: progressAnimation 2s ease-in-out infinite;
  }
  
  @keyframes progressAnimation {
    0% { width: 0%; }
    50% { width: 70%; }
    75% { width: 85%; }
    90% { width: 90%; }
    100% { width: 95%; }
  }
  
  /* Handling reduced motion preferences */
  @media (prefers-reduced-motion: reduce) {
    .animate-bounce-slow {
      animation: none;
    }
    .loading-progress {
      animation: none;
      width: 70%;
    }
  }
  </style>